import { MMS_CONSTANTS, ERROR_MESSAGES } from './sms-constants';
import { parsePhoneNumberFromString, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';
import { Descendant, Element as SlateElement } from 'slate';
import { isEmail } from 'validator';

export type ToastFunction = (props: {
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
}) => void;

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Constants for image compression
 */
const IMAGE_COMPRESSION = {
  /** Maximum dimension for compressed images (512px) */
  MAX_DIMENSION: 512,
  /** JPEG quality for compressed images (0.8 = 80%) */
  JPEG_QUALITY: 0.8
} as const;

/**
 * Compresses an image by resizing it and reducing quality.
 * Maintains aspect ratio and converts to JPEG format.
 *
 * @param file - The image file to compress
 * @returns A promise that resolves to the compressed image as a base64 string
 */
export function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > IMAGE_COMPRESSION.MAX_DIMENSION) {
          height = Math.round((height * IMAGE_COMPRESSION.MAX_DIMENSION) / width);
          width = IMAGE_COMPRESSION.MAX_DIMENSION;
        }
      } else {
        if (height > IMAGE_COMPRESSION.MAX_DIMENSION) {
          width = Math.round((width * IMAGE_COMPRESSION.MAX_DIMENSION) / height);
          height = IMAGE_COMPRESSION.MAX_DIMENSION;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to base64 with reduced quality
      const base64 = canvas.toDataURL('image/jpeg', IMAGE_COMPRESSION.JPEG_QUALITY);
      URL.revokeObjectURL(img.src); // Clean up
      resolve(base64);
    };
    img.onerror = reject;
  });
}

/**
 * Validates a file against MMS requirements.
 * Checks file size and type.
 *
 * @param file - The file to validate
 * @returns object containing validation result and error message if any
 */
export function validateMMSFile(file: File): { isValid: boolean; errorMessage: string | null } {
  if (file.size > MMS_CONSTANTS.MAX_FILE_SIZE) {
    return { isValid: false, errorMessage: ERROR_MESSAGES.FILE_TOO_LARGE };
  }

  if (
    !MMS_CONSTANTS.SUPPORTED_FILE_TYPES.includes(
      file.type as (typeof MMS_CONSTANTS.SUPPORTED_FILE_TYPES)[number]
    )
  ) {
    return { isValid: false, errorMessage: ERROR_MESSAGES.INVALID_FILE_TYPE };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates an email address using the validator library.
 *
 * @param email - The email address to validate
 * @returns ValidationResult indicating if the email is valid
 */
export function validateEmail(email: string): ValidationResult {
  if (!isEmail(email)) {
    return { isValid: false, errorMessage: 'Please enter a valid email address' };
  }
  return { isValid: true, errorMessage: null };
}

/**
 * Validates a phone number using libphonenumber-js.
 * Attempts to parse the number and checks its validity.
 *
 * @param phoneNumber - The phone number to validate
 * @param locale - The locale to use for validation (defaults to US)
 * @returns ValidationResult indicating if the phone number is valid
 */
export function validatePhoneNumber(
  phoneNumber: string,
  locale: CountryCode = 'US'
): ValidationResult {
  try {
    const parsedNumber = parsePhoneNumberFromString(phoneNumber, locale);

    if (!parsedNumber || !isValidPhoneNumber(phoneNumber, locale)) {
      return {
        isValid: false,
        errorMessage: 'Please enter a valid phone number'
      };
    }

    return { isValid: true, errorMessage: null };
  } catch (_error) {
    return {
      isValid: false,
      errorMessage: 'Unable to validate phone number format'
    };
  }
}

/**
 * Checks if the editor value contains any MMS content (images or contact cards).
 *
 * @param value - The editor value to check
 * @returns boolean indicating if the content contains MMS elements
 */
export function hasMMSContent(value: Descendant[]): boolean {
  return value.some(
    (node) =>
      SlateElement.isElement(node) &&
      // MMS content types
      (node.type === 'image' || node.type === 'contact-card')
  );
}
